import { useState } from "react";
import type { RejectedOperation } from "@tovi/pos-sync";
import { Badge, Button, Modal, useTranslate } from "@tovi/ui";
import { useTill } from "@/lib/use-till.ts";

/**
 * What the server refused, and what a cashier does about it.
 *
 * The till has always counted these — "3 could not be sent" sat in the mode
 * badge — and had nowhere to show them. A count that cannot be opened tells a
 * cashier that something is wrong and nothing about what, which on a refused
 * settlement is the difference between a bill somebody chases and a bill
 * nobody knew about.
 *
 * They come out of the queue's dead-letter store, so this list is the same
 * after a reload as it was before one. Dismissing is deliberate and per-row:
 * nothing about time or a restart makes a refused payment dealt with, so the
 * only way one leaves is a person saying so.
 */
export function FailureList() {
  const t = useTranslate();
  const { till, state } = useTill();
  const [open, setOpen] = useState(false);

  const failures = till.failures;
  if (failures.length === 0 && state.kind !== "needsAttention") return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-control border border-line px-3 py-1"
      >
        <Badge tone="danger">{t("pos.sync.failed", { count: failures.length })}</Badge>
      </button>

      {open ? (
        <Modal open title={t("pos.failures.title")} onClose={() => setOpen(false)}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-ink-muted">{t("pos.failures.intro")}</p>

            <ul className="flex flex-col gap-3">
              {failures.map((failure) => (
                <FailureRow
                  key={failure.operation.id}
                  failure={failure}
                  onDismiss={() => void till.dismiss(failure.operation.id)}
                />
              ))}
            </ul>

            {failures.length === 0 ? (
              <p className="text-sm text-ink-muted">{t("pos.failures.none")}</p>
            ) : null}

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setOpen(false)}>
                {t("pos.failures.close")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function FailureRow({ failure, onDismiss }: { failure: RejectedOperation; onDismiss: () => void }) {
  const t = useTranslate();
  const { operation } = failure;

  return (
    <li className="flex flex-col gap-2 rounded-control border border-line p-3">
      <div className="flex items-center gap-2">
        {/* A settlement is called out, because it is the one a cashier has to
            act on today rather than at the end of service. */}
        {operation.isMoney ? (
          <Badge tone="danger">{t("pos.failures.money")}</Badge>
        ) : (
          <Badge tone="warning">{t("pos.failures.change")}</Badge>
        )}
        <span className="text-xs text-ink-muted">
          {t("pos.failures.queuedAt", { at: formatWhen(operation.queuedAt) })}
        </span>
      </div>

      {/* The server's own words. Not translated: it is a specific reason for a
          specific bill, and a generic message here would lose the only thing
          on this row that says what to do. */}
      <p className="text-sm text-ink-body">{failure.message}</p>

      <p className="font-mono text-xs text-ink-muted">
        {operation.method} {operation.path}
        {failure.status > 0 ? ` · ${failure.status}` : ""}
      </p>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={onDismiss}>
          {t("pos.failures.dismiss")}
        </Button>
      </div>
    </li>
  );
}

/** The cashier's clock, not an ISO string on a receipt. */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}
